import React, { FunctionComponent } from 'react';
import { Redirect, Route, RouteProps, useLocation } from 'react-router-dom';

import useTypedSelector from 'hooks/useTypedSelector';
import {
  isUserLoggedInSelector,
  userRolesSelector,
  isUserVerifiedSelector,
} from 'redux/selectors/auth.selector';
import { getCookie } from 'helpers/cookies';
import { ROLES } from 'helpers/roles';
import { Box, CircularProgress } from '@mui/material';

interface IRouteContainerProps extends RouteProps {
  authenticated?: boolean;
  isPublicRoute?: boolean;
  restricted?: boolean;
  verified?: boolean;
  roles?: Array<string>;
}

const RouteContainer: FunctionComponent<IRouteContainerProps> = ({
  component: Component,
  authenticated = false,
  isPublicRoute = false,
  restricted = false,
  verified = false,
  roles = [],
  ...rest
}) => {
  const location = useLocation();
  const isUserLoggedIn = useTypedSelector(isUserLoggedInSelector);
  const userRoles = useTypedSelector(userRolesSelector);
  const isUserVerified = useTypedSelector(isUserVerifiedSelector);
  const isInitializing = useTypedSelector((state) => state.auth.loading);

  if (!Component) return null;

  if (isInitializing) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100vw',
          position: 'fixed',
          top: 0,
          left: 0,
          backgroundColor: '#ffffff',
          zIndex: 9999,
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: '#0057ff' }} />
      </Box>
    );
  }

  if (restricted && (getCookie('accessToken') || isUserLoggedIn)) {
    return <Redirect to={{ pathname: isPublicRoute ? '/public/procedures' : '/' }} />;
  }

  if (authenticated && !getCookie('accessToken') && !isUserLoggedIn) {
    return (
      <Redirect
        to={{
          pathname: isPublicRoute ? '/public/procedures/login' : '/login',
          state: { from: location },
        }}
      />
    );
  }

  const isClient = userRoles.includes('cliente');
  if (verified && isUserLoggedIn && !isUserVerified && isClient) {
    return <Redirect to={{ pathname: '/public/procedures' }} />;
  }
  if (verified && !isUserLoggedIn) {
    return <Redirect to={{ pathname: '/public/procedures/login', state: { from: location } }} />;
  }

  if (
    isUserLoggedIn &&
    roles.length > 0 &&
    !userRoles.some((userRole) => roles.includes(userRole))
  ) {
    return (
      <Redirect
        to={{
          pathname: userRoles.includes(ROLES.cliente) ? '/public/procedures' : '/',
        }}
      />
    );
  }

  return <Route {...rest} render={(props) => <Component {...props} />} />;
};

export default RouteContainer;
